import {
    getLicensingViewOrManagePermissions,
    getTenantLicensingViewPermissions,
    PermissionAction,
} from '@experiences/interfaces';

import { useHasAnyPermission } from '../../container/UserAuthPDPModelProvider/UserAuthPDPModelProvider';

/**
 * True when the user may **mutate** licensing (allocate, deallocate, generate keys, migration actions, etc.).
 * Organization admins: `useHasAnyPermission` returns true without checking PDP.
 * Others: AuthZ **LicensingData** / **MANAGE** on PDP. View-only (no Manage) roles get false (read-only UI).
 */
export const useCanManageLicensingData = (): boolean =>
    useHasAnyPermission([ PermissionAction.Licensing.LicensingData.Manage ]);

/**
 * True when the user has Licensing **View** or **Manage** on PDP (or org admin short-circuit). **Access** alone is false.
 * Use with {@link useCanManageLicensingData}: show the same Users / Robots tabs and data as admins, but keep
 * grids and migration actions disabled when Manage is missing.
 */
export const useHasAnyLicensingPermission = (): boolean =>
    useHasAnyPermission(getLicensingViewOrManagePermissions());

/**
 * True when the user holds tenant-scoped Licensing read access (`Tenant Licensing Viewer`,
 * or any org-scoped Licensing Viewer/Admin via the rolled-up action). When `tenantId` is
 * provided, the check requires the action at that specific tenant's scope; without it, the
 * user must hold the action at any scope (useful for showing/hiding menu entries before a
 * tenant is known). The role/action assignment itself is the gate — there is no separate
 * feature flag.
 *
 * @param tenantId - Optional tenant scope to check the action against; omit for any-scope.
 */
export const useCanViewTenantLicensing = (tenantId?: string): boolean =>
    useHasAnyPermission(getTenantLicensingViewPermissions(), tenantId ?? '');
